import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { AdditionalField } from 'src/app/shared/models/additional-field.model';
import { alphaNumeric } from '../../../../shared/validators/alphanumeric.validators';
import { whitespace } from '../../../../shared/validators/whitespace.validator';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { CurrentOrderService } from 'src/app/shared/services/current-order.service';
import { AdditionalFieldHelper } from '../../../../shared/services/additional-field-helper.service';
import { FieldCriteria } from 'src/app/shared/models/order-template-type.model';
import { PriorityLevel } from 'src/app/shared/models/priority-level.model';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { StoreService, TranslationService } from 'projects/dop-core/src/public-api';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { OrderTemplate } from 'src/app/shared/models/order-template.model';
import { UsePriority } from 'src/app/shared/enums/use-priority.enum';
import { STD, AFS, RUSH, Standard, TOPPRIORITY, Top, Rush } from '../../samples/constants/sample-constants';
import { MessageService } from 'primeng/api';
import { StringFormatter } from 'src/app/shared/pipes/string-formatter.pipe';
import { VALUE } from '../../samples/constants/sample-constants';
import { Recipient } from 'src/app/shared/models/recipient.model';
import { OrderState } from 'src/app/shared/enums/order-state.enum';
import { throwError } from 'rxjs';
import { AdditionalOrderDetailsValidator } from 'src/app/shared/order-validator/validators/additional-order-details.validator';
import { LabType } from 'src/app/shared/enums/lab-type.enum';
import { LabConfigurationCommandService } from 'src/app/shared/data-service/lab-configuration-command.service';
import { AttachDocument } from 'src/app/shared/models/attach-document';
import { SelectedFile } from 'src/app/shared/models/selected-file';
import { ScanStatus } from 'src/app/shared/enums/scan-status.enum';
import { FileStatus } from 'src/app/shared/enums/file-status.enum';
import { DocumentType } from '../../../../shared/enums/document-type.enum';
import { SwitchStepService } from 'src/app/core/services/switch-step.service';
import { AccountQuotationRecipient } from 'src/app/shared/models/account-quotation-recipient';

@Injectable({
  providedIn: 'root'
})
export class AddOrderDetailService {
  fb: FormBuilder;
  enableTodayAsDefaultValueForDateTime = false;
  usePriority: number;
  enableReportGrouping: boolean;
  get CustomerReferenceFieldCriteria(): FieldCriteria {
    return this.currentOrder.orderTemplate.usePurchaseOrderReference;
  }

  get displayPriorityLevels(): boolean {
    const sample = this.currentOrder.samples[0];
    return !sample?.analyticalReportGroupingRule && this.currentOrder.orderTemplate.usePriority === UsePriority.ORDER;
  }

  get currentOrder() { return this.currentOrderService.currentOrder; }

  constructor(
    private additionalFieldHelper: AdditionalFieldHelper,
    public orderCmdClient: OrderCommandService,
    private currentOrderService: CurrentOrderService,
    private orderDataService: OrderDataService,
    private storeService: StoreService,
    private switchStepService: SwitchStepService,
    private cmdClient: OrderCommandService,
    private messageService: MessageService,
    private labConfigurationCmdClient: LabConfigurationCommandService) {
  }

  async initiate(fb: FormBuilder): Promise<FormGroup> {
    const orderTemplate: OrderTemplate = await this.storeService.Get(StoreKey.SelectedOrderTemplate).toPromise();
    if (orderTemplate) {
      this.enableReportGrouping = orderTemplate.enableReportGrouping;
      this.usePriority = orderTemplate.usePriority;
    } else {
      this.enableReportGrouping = this.currentOrder.orderTemplate.enableReportGrouping;
      this.usePriority = this.currentOrder.orderTemplate.usePriority;
    }


    this.fb = fb;
    let afs: AdditionalField[] = [];

    // this code is written specifically for older orders which were not created with mandatory AR recipients. Ensures proper flow without any errors.
    if (this.currentOrder.orderState === OrderState.FavoriteOrder) {
      afs = this.additionalFieldHelper.getOrderMandatoryAfs();
      afs = afs.concat(this.additionalFieldHelper.getOrderOptionalAfs());
      if (afs.length > 0 && this.currentOrder.additionalFields.length > 0
        && this.currentOrder.additionalFields.every(item => item.defaultValue === null || item.defaultValue === undefined)) {
        afs.forEach(item => {
          const af = this.currentOrder.additionalFields.find(x => x.code === item.code);
          if (af) {
            item.value = af.value;
          }
        });
        this.currentOrder.additionalFields = afs.filter(x => this.currentOrder.additionalFields.find(y => y.code === x.code));
      }
    }
    else if (this.currentOrder.orderState !== OrderState.Pending &&
      this.currentOrder.orderState !== OrderState.EditFavoriteOrder &&
      this.currentOrder.orderState !== OrderState.Submitted) {
      afs = this.additionalFieldHelper.getOrderMandatoryAfs();
      if (afs.length > 0 && !this.currentOrder.additionalFields) {
        this.currentOrder.additionalFields = afs;
        this.cmdClient.addOrderAfs(this.currentOrder.OrderCode, this.additionalFieldHelper.buildAfDtoList(afs)).toPromise()
          .then(() => { });
      }
    }
    return this.getOrderForm(this.currentOrder.additionalFields);
  }

  async loadNimsRecipients(callback: () => void) {
    let nimsRecipients: Recipient[];
    await this.orderDataService
      .getNIMSRecipient(this.currentOrder.account.code,
        this.currentOrderService.isElimsLab, this.currentOrder.quotation.code, this.currentOrder.contract.code)
      .toPromise().then(res => {
        nimsRecipients = res;
      }).catch(error => {
        if (error.status !== 500) {
          callback();
        } else {
          return throwError(error);
        }
      });
    callback();
    return nimsRecipients;
  }

  getRecipients(recipientArray: FormArray, callback: () => void) {
    if (this.currentOrder.orderState !== OrderState.Pending &&
      this.currentOrder.orderState !== OrderState.EditFavoriteOrder &&
      this.currentOrder.orderState !== OrderState.Submitted &&
      this.currentOrder.orderState !== OrderState.FavoriteOrder) {
      if (this.currentOrder.isNimsRecipientLoadedForNewOrder || this.currentOrder.isELIMSRecipientLoadedForNewOrder) {
        callback();
        return;
      }

        this.storeService.Get<AccountQuotationRecipient>(StoreKey.ReadonlyRecipients).toPromise().then(async result => {
          if (result && result?.accountCode === this.currentOrder?.AccountCode && result?.quotationCode === this.currentOrder?.QuotationCode && result?.recipients?.length > 0) {
            result.recipients.map(x => x.id = undefined);
            this.onLoadOfRecipient(result?.recipients, recipientArray, () => callback());
            this.currentOrder.isNimsRecipientLoadedForNewOrder = true;
          } else {
            const res = await this.loadNimsRecipients(callback);
            const newRecipients = this.currentOrderService.currentOrder.recipients ?
              res.filter(x => !this.currentOrderService.currentOrder.recipients.some(y => y.email === x.email)) : res;
  
            if (newRecipients && newRecipients.length > 0) {
              var recipientsToSave = new AccountQuotationRecipient(this.currentOrder?.AccountCode, this.currentOrder?.QuotationCode, res);
              this.storeService.Save(StoreKey.ReadonlyRecipients, recipientsToSave, true);
              this.onLoadOfRecipient(res, recipientArray, () => callback());
              this.currentOrder.isNimsRecipientLoadedForNewOrder = true;
            }
          }
        });
    }
    callback();
  }

  private onLoadOfRecipient(recipients: Recipient[], recipientArray: FormArray, callback: () => void) {
    if (recipients && recipients.length > 0) {
      this.addUpdateRecipients(recipients, recipientArray, () => callback());
    }
  }

  savePONumber(poNumber: string, callback: () => void) {
    this.labConfigurationCmdClient.addUpdateDefaultPurchaseOrder(this.currentOrder.account.code, poNumber).toPromise().then(_ => {
      callback();
    });
  }

  deletePONumber(callback: () => void) {
    this.labConfigurationCmdClient.deleteDefaultPurchaseOrder(this.currentOrder.account.code).toPromise().then(_ => {
      this.currentOrderService.currentOrder.defaultPONumber = undefined;
      this.currentOrderService.currentOrder.CustomerReference = '';
      callback();
    });
  }

  addUpdateRecipients(recipients: Recipient[], recipientGroup: FormArray, callback: () => void) {
    this.orderCmdClient.addUpdateRecipient(this.currentOrder.OrderCode, recipients).toPromise().then(x => {
      if (this.currentOrder.recipients && this.currentOrder.recipients.length > 0) {
        x.forEach(xy => {
          const existing = this.currentOrder.recipients.find(xyz => (xyz.id === xy.id) || (xyz.email === xy.email));
          if (existing) {
            existing.analyticalReport = xy.analyticalReport;
            existing.email = xy.email;
            existing.firstName = xy.firstName;
            existing.middleName = xy.middleName;
            existing.lastName = xy.lastName;
            existing.analyticalReportCopy = xy.analyticalReportCopy;
            existing.contactType = xy.contactType;
            existing.invoice = xy.invoice;
            existing.informationToSend = [];
            if (existing.analyticalReportCopy) {
              existing.informationToSend.push({ documentType: DocumentType.AnalyticalReport });
            }

            if (existing.analyticalReport) {
              existing.informationToSend.push({ documentType: DocumentType.AnalyticalReportCopy });
            }

            if (existing.invoice) {
              existing.informationToSend.push({ documentType: DocumentType.InvoiceDocument });
            }

          } else {
            this.currentOrder.recipients.push({ ...xy });
            recipientGroup.push(this.buildRecipient(xy));
          }
        });
      } else {
        this.currentOrder.recipients = [];
        x.forEach(element => {
          recipientGroup.push(this.buildRecipient(element));
          this.currentOrder.recipients.push({ ...element });
        });
      }
      const checkARSelected = this.currentOrderService.currentOrder.recipients.find(x => x.informationToSend.find(y => y.documentType == 2 || y.documentType == 3));
      if (checkARSelected)
      {
        this.currentOrderService.currentOrder.hasUserAddedRecipientsError = false;
        this.switchStepService.clearRecipientErrorMessage(true);
      }
      callback();
    });
  }

  deleteRecipient(id: string, recipientArray: FormArray) {
    this.orderCmdClient.deleteRecipient(this.currentOrder.OrderCode, id).toPromise().then(_ => {
      this.currentOrder.recipients.splice(this.currentOrder.recipients.findIndex(x => x.id === id), 1);
      const index = recipientArray.controls.findIndex(x => x.get('id').value === id);
      recipientArray.removeAt(index);
    });
  }

  buildAFs(afs: AdditionalField[]): FormArray {
    const grp: FormArray = this.fb.array([]);
    if (afs && afs.length > 0) {
      afs.forEach((af: AdditionalField) => {
        grp.push(this.buildAdditionalField(af));
      });
    }

    grp.controls = this.currentOrderService.SortAdditionalFieldsFormArray(grp.controls);
    return grp;
  }

  buildAdditionalField(af: AdditionalField): FormGroup {
    return this.fb.group({
      code: af.code,
      name: af.name,
      defaultValue: af.defaultValue,
      value: this.buildAfValue(af),
      fieldType: af.fieldType,
      isMandatory: af.isMandatory,
      isUnique: af.isUnique,
      scope: af.scope,
      additionalFieldListValues: this.fb.array(af.additionalFieldListValues),
    });
  }

  buildRecipients(recipients: Recipient[]): FormArray {
    const grp: FormArray = this.fb.array([]);
    if (recipients && recipients.length > 0) {
      recipients.forEach((recipient: Recipient) => {
        grp.push(this.buildRecipient(recipient));
      });
    }

    return grp;
  }

  buildRecipient(recipient: Recipient): FormGroup {
    return this.fb.group({
      code: recipient.code,
      id: recipient.id,
      name: this.name(recipient),
      firstName: recipient.firstName,
      middleName: recipient.middleName,
      lastName: recipient.lastName,
      email: recipient.email,
      invoice: recipient.invoice && recipient.isReadOnly,
      analyticalReport: recipient.analyticalReport,
      analyticalReportCopy: recipient.analyticalReport || recipient.analyticalReportCopy,
      isReadOnly: recipient.isReadOnly,
      contactType: recipient.contactType
    });
  }

  isError(af: FormGroup): boolean {
    const errors = af.get(VALUE).errors;
    return (errors && (errors.alphaNumeric || errors.unique || errors.whitespace || errors.required || errors.numeric));
  }

  validateSaveOrderAf(orderCode: string, af: FormGroup) {
    this.additionalFieldHelper.formatFloatAf(af);
    if (!this.isError(af)) {
      this.additionalFieldHelper.setAfValue(af);
      if (af.value.value !== null && af.value.value !== undefined && af.value.fieldType === 'TXT') {
        af.value.value = af.value.value.trim();
      }

      if (af.value.value !== this.currentOrder.additionalFields.find(x => x.code === af.value.code).value) {
        this.patchAdditionalField(orderCode, af.value as AdditionalField);
      }
    }
  }

  private patchAdditionalField(orderCode: string, af: AdditionalField) {
    this.cmdClient.patchOrderAdditionalField(orderCode, af).toPromise().then(() => {
      const afValue = this.currentOrder.additionalFields.find(x => x.code === af.code);
      if (afValue) {
        afValue.value = af.value;
        af.value = af.value;
      }
    });
  }

  addOrUpdateAdditionalField(adfs: AdditionalField[], order: FormGroup, callback: (flag: boolean) => boolean) {
    if (adfs) {
      // Extract newly added af from selected optional af list
      const newAfs = this.additionalFieldHelper.getNewlyAddedAf(order, adfs);

      // Save optional AF for order
      if (newAfs.length > 0) {
        this.cmdClient.addOrderAfs(this.currentOrder.OrderCode,
          this.additionalFieldHelper.buildAfDtoList(newAfs)).subscribe(() => {
            const afs = order.get(AFS) as FormArray;
            newAfs.forEach(af => {
              afs.push(this.buildAdditionalField(af)); // add af in reactive form
              if (!this.currentOrderService.currentOrder.additionalFields) {
                this.currentOrderService.currentOrder.additionalFields = [];
              }
              this.currentOrderService.currentOrder.additionalFields.push({ ...af }); // add af in sample of current order object
            });
            afs.controls = this.currentOrderService.SortAdditionalFieldsFormArray(afs.controls);
            if (this.currentOrderService.currentOrder.additionalFields.filter(x => x.isMandatory === false).length ===
              this.additionalFieldHelper.getOrderOptionalAfs().length) {
              callback(true);
            }
          });
      }
    }
  }

  getOrderForm(afs: AdditionalField[]) {
    const grp = this.fb.group({
      orderCode: this.currentOrder.OrderCode,
      customerReference: [this.currentOrder.CustomerReference, [Validators.maxLength(50), alphaNumeric()]],
      customerComment: [this.currentOrder.CustomerComment, [Validators.maxLength(2000)]],
      priorityLevel: this.currentOrder.samples[0].priorityLevel,
      analyticalReportGrouping: this.currentOrder.samples[0].analyticalReportGroupingRule,
      afs: this.buildAFs(afs),
      recipients: this.buildRecipients(this.currentOrder.recipients),
      attachDocuments: this.buildAttachments(this.currentOrder.attachDocuments)
    });

    if (this.currentOrder.orderTemplate.usePurchaseOrderReference === FieldCriteria.Mandatory) {
      grp.get('customerReference').clearValidators();
      grp.get('customerReference').setValidators([Validators.required, Validators.maxLength(50), alphaNumeric(), whitespace()]);
      grp.get('customerReference').updateValueAndValidity();
    } else if (this.currentOrder.orderTemplate.usePurchaseOrderReference === FieldCriteria.Hidden) {
      grp.get('customerReference').setValue('');
      this.currentOrder.CustomerReference = null;
    }

    return grp;
  }

  buildAttachments(attachDocuments: AttachDocument[]): FormArray {
    const grp: FormArray = new FormBuilder().array([]);
    if (attachDocuments) {
      attachDocuments.forEach((attachment: AttachDocument) => {
        grp.push(this.buildAttachment(attachment));
      });
    }
    return grp;
  }

  buildAttachment(attachment: AttachDocument): FormGroup {
    return new FormBuilder().group({
      icon: attachment.icon,
      internalFileName: attachment.internalFileName,
      name: attachment.name,
      status: attachment.status,
      fileStatus: attachment.fileStatus
    });
  }

  addDocumentInCurrentOrderAndReactiveForm(files: SelectedFile[], documentArray: FormArray) {
    if (files && files.length > 0) {
      files.forEach(file => {
        const tempDocument: AttachDocument = {
          name: file.name,
          internalFileName: file.internalFileName,
          icon: file.icon,
          status: file.status,
          fileStatus: FileStatus.None
        };

        if (!this.currentOrder.attachDocuments) {
          this.currentOrder.attachDocuments = [];
        }

        this.currentOrder.attachDocuments.push(tempDocument);
        documentArray.push(this.buildAttachment(tempDocument));
      });
    }
  }

  patchOrderField(propertyName: string, value: string) {
    let callback: () => void;
    if (value) {
      value = value.trim();
    }

    if (propertyName === 'customerReference' && this.currentOrder.CustomerReference !== value) {
      callback = () => {
        this.currentOrder.CustomerReference = value;
      };
    } else if (propertyName === 'customerComment' && this.currentOrder.CustomerComment !== value) {
      callback = () => {
        this.currentOrder.CustomerComment = value;
      };
    } else {
      callback = () => {
        return;
      };
    }

    this.orderCmdClient.patchOrderDetails(this.currentOrder.OrderCode, [{ name: propertyName, value }]).toPromise().then(_ => {
      callback();
    });
  }

  patchBulkSample(name: string, value: string, sampleCodes: string[], callback: () => void) {
    this.orderCmdClient.patchBulkSample(sampleCodes, name, value).toPromise().then(x => {
      if (x) {
        this.currentOrder.samples.forEach(sample => {
          sample.priorityLevel = value;
        });
        if (callback) {
          callback();
        }
      }
    });
  }

  getPriorityLevels(): Promise<PriorityLevel[]> {
    return this.orderDataService.getPriorityLevels().toPromise().then(data => {
      return data;
    });
  }

  getSelectedPriority(): string {
    return this.currentOrder.samples[0].priorityLevel ? this.currentOrder.samples[0].priorityLevel : STD;
  }

  getPriorityLevelDisplayName() {
    const priorityLevel = this.getSelectedPriority();
    switch (priorityLevel) {
        case STD:
            return Standard;
        case RUSH:
            return Rush;
        case TOPPRIORITY:
            return Top;
    }
  }

  private buildAfValue(af: AdditionalField): any {
    return [!(this.isDateType(af.fieldType) && this.enableTodayAsDefaultValueForDateTime)
      ? (this.isDateType(af.fieldType) ? (af.value ? this.setDefaultDate(af.value)
        : this.setDefaultDate(af.defaultValue)): af.value) : af.value,
      { validators: this.additionalFieldHelper.setAdditionalFieldValidators(af) }];
  }

  private isDateType(type: string): boolean {
    return (type === 'DT' || type === 'TMO' || type === 'DTO');
  }

  private setDefaultDate(value: string): Date {
    if (value) {
      return new Date(value);
    } else {
      if(this.enableTodayAsDefaultValueForDateTime)
        return new Date();
      else 
        return null;
    }
  }

  deleteAf(additionalField: AdditionalField, orderForm: FormGroup, callback: () => void) {
    this.orderCmdClient.deleteAfFromOrder(this.currentOrder.OrderCode, additionalField.code).toPromise().then(_ => {
      const adf = this.currentOrder.additionalFields.findIndex(x => x.code === additionalField.code);
      this.currentOrder.additionalFields.splice(adf, 1);
      const afs = (orderForm.get(AFS) as FormArray);
      const index = afs.controls.findIndex(x => x.get('code').value === additionalField.code);
      afs.removeAt(index);
      this.showToaster({
        key: 'deleteAdditionalfieldSuccess',
        severity: 'success',
        detail: StringFormatter.format(TranslationService.translate('vbeiAdditionalFieldDeletionSuccess'), additionalField.name)
      });
    });
    if (callback) {
      callback();
    }
  }

  private showToaster(data: { key: string, severity: string, summary?: string, detail: string }) {
    this.messageService.add({ key: data.key, severity: data.severity, summary: data.summary, detail: data.detail });
  }

  name(recipient: Recipient) {
    if (!recipient.firstName) {
      return recipient.lastName;
    } else if (!recipient.lastName) {
      return recipient.firstName;
    } else if (recipient.middleName){
      return recipient.firstName + ' ' + recipient.middleName + ' ' + recipient.lastName;
    } else {
      return recipient.firstName + ' ' + recipient.lastName;
    }
  }

  async validateOrder() {
      let recipientResult = await this.storeService.Get<AccountQuotationRecipient>(StoreKey.ReadonlyRecipients).toPromise();
      let nimsRecipients = recipientResult?.recipients;
      if (!nimsRecipients || recipientResult?.accountCode !== this.currentOrderService?.currentOrder?.AccountCode || recipientResult?.quotationCode !== this.currentOrderService?.currentOrder?.QuotationCode) {
        nimsRecipients = await this.loadNimsRecipients(() => { });
      }
      if (nimsRecipients && nimsRecipients.length > 0) {
        if (this.currentOrderService.currentOrder.recipients && this.currentOrderService.currentOrder.recipients.length > 0) {
          this.currentOrderService.currentOrder.recipients.map(r => {
            const readOnlyRecipients = nimsRecipients.filter(nr => nr.email === r.email).shift();
            r.isReadOnly = readOnlyRecipients ? readOnlyRecipients.isReadOnly : false;
          });
        }
      }
      const additionalOrderValidator = new AdditionalOrderDetailsValidator(this.currentOrderService.currentOrder, nimsRecipients);
      const result = await additionalOrderValidator.validate();
      return result;
  }

  async setRecipientsReadOnly() {
    let recipientResult = await this.storeService.Get<AccountQuotationRecipient>(StoreKey.ReadonlyRecipients).toPromise();
    let nimsRecipients = recipientResult?.recipients;
    if (!nimsRecipients || recipientResult?.accountCode !== this.currentOrderService?.currentOrder?.AccountCode || recipientResult?.quotationCode !== this.currentOrderService?.currentOrder?.QuotationCode) {
      nimsRecipients = await this.loadNimsRecipients(() => { });
    }
    if (nimsRecipients && nimsRecipients.length > 0) {
      if (this.currentOrderService.currentOrder.recipients && this.currentOrderService.currentOrder.recipients.length > 0) {
        this.currentOrderService.currentOrder.recipients.map(r => {
          const readOnlyRecipients = nimsRecipients.filter(nr => nr.email === r.email).shift();
          r.isReadOnly = readOnlyRecipients ? readOnlyRecipients.isReadOnly : false;
        });
      }
    }
  }
}
